import { objectSpread2 as _objectSpread2 } from '../../_virtual/_rollupPluginBabelHelpers.js';
import PendoButton from '../../components/button/pendo-button.vue.js';
import PendoModal from '../../components/modal/pendo-modal.vue.js';
import PendoColumnReorder from '../../components/table/column-reorder/pendo-column-reorder.vue.js';
import PendoMultiselect from '../../components/multiselect/pendo-multiselect.vue.js';
import PendoIconOption from '../../components/multiselect/option-types/pendo-icon-option.vue.js';
import { isProductOption, groupOptionsByKind, getResourceOptions, getHasPolls, getAvailableColumns, getFactOptionsForType } from './utils.js';
import uuid from 'uuid';
import get from 'lodash/get.js';
import startCase from 'lodash/startCase.js';
import ResourceSelector from './resource-selector.vue.js';
import PendoTooltip from '../../directives/tooltip/pendo-tooltip.js';
import PendoIcon from '../../components/icon/pendo-icon.vue.js';

var script = {
  name: 'PendoResourceColumnChooser',
  components: {
    ResourceSelector,
    PendoButton,
    PendoModal,
    PendoColumnReorder,
    PendoMultiselect,
    PendoIconOption,
    PendoIcon
  },
  directives: {
    PendoTooltip
  },
  props: {
    columns: {
      type: Array,
      required: true
    },
    featuresList: {
      type: Array,
      default: () => []
    },
    guidesList: {
      type: Array,
      default: () => []
    },
    pagesList: {
      type: Array,
      default: () => []
    },
    visible: {
      type: Boolean,
      default: false
    },
    saving: {
      type: Boolean,
      default: false
    },
    isLoadingResources: {
      type: Boolean,
      default: false
    },
    isLoadingMetadata: {
      type: Boolean,
      default: false
    },
    metadataList: {
      type: Array,
      default: () => []
    },
    modalTitle: {
      type: String,
      default: 'Manage Columns'
    },
    allSelectedText: {
      type: String,
      default: 'All available columns selected.'
    },
    addButtonLabel: {
      type: String,
      default: 'Add column'
    },
    optionPlaceholder: {
      type: String,
      default: 'Select column'
    },
    maxColumnSelections: {
      type: Number,
      default: undefined
    },
    modalWidth: {
      type: [Number, String],
      default: 400
    }
  },
  data() {
    return {
      localColumns: this.columns,
      renderColumnReorder: false,
      toggleModal: false
    };
  },
  computed: {
    disableAddColumn() {
      if (this.maxColumnSelections) {
        return this.selectedColumns.length === this.maxColumnSelections;
      }
      return !this.availableOptions.length;
    },
    availableOptions() {
      const available = this.allColumnOptions.filter(option => {
        return isProductOption(option) || !this.selectedColumns.find(column => column.id === option.id);
      });
      return groupOptionsByKind(available);
    },
    pages() {
      return getResourceOptions(this.pagesList, 'pageId');
    },
    features() {
      return getResourceOptions(this.featuresList, 'featureId');
    },
    guides() {
      return getResourceOptions(this.guidesList, 'guideId');
    },
    polls() {
      const guidesWithPolls = this.guidesList.filter(guide => {
        const isResourceCenter = get(guide, 'attributes.resourceCenter') && guide.state !== 'public';
        return !isResourceCenter && getHasPolls(guide);
      });
      return getResourceOptions(guidesWithPolls, 'guideId');
    },
    availableColumns() {
      return groupOptionsByKind(this.allColumnOptions);
    },
    selectedColumns() {
      return this.localColumns.filter(column => column.allowReorder !== false).map((column, index) => {
        let {
          id,
          name
        } = column;
        const {
          prop
        } = column;
        if (isProductOption(column)) {
          const type = column.type === 'poll' ? 'guide' : column.type;
          const resourceId = (column[`${type}Id`] || '').replace(/-/g, '_') || uuid();
          const fact = column.type === 'poll' ? column.pollId : column.fact;
          id = `${column.type}_${resourceId}_${fact}_${index}`;
        } else if (prop) {
          id = prop;
          name = column.label;
        }
        return _objectSpread2(_objectSpread2({}, column), {}, {
          id,
          name,
          disabled: column.immutable
        });
      });
    },
    allColumnOptions() {
      return getAvailableColumns(this.metadataList);
    }
  },
  methods: {
    onModalClosed() {
      this.renderColumnReorder = false;
    },
    addColumn() {
      if (this.disableAddColumn) {
        return;
      }
      const columns = [...this.localColumns, {}];
      this.updateColumns(columns);
    },
    initLocalColumns() {
      this.localColumns = this.columns;
      setTimeout(() => {
        this.renderColumnReorder = true;
      }, 0);
    },
    updateColumns(value) {
      this.localColumns = value;
    },
    onColumnSelect(column, index, handleColumnSelect) {
      if (isProductOption(column)) {
        this.$emit('fetch-resources', column.type);
      }
      handleColumnSelect(column, index);
    },
    handleResourceSelectorColumnSelect(model, index, propName, prop, handleColumnSelect) {
      const column = _objectSpread2(_objectSpread2({}, model[index]), {}, {
        [propName]: prop
      });
      handleColumnSelect(column, index);
    },
    isProductOption,
    factSelectorPropsForColumn(model, index) {
      const column = model[index];
      const options = getFactOptionsForType(column.type);
      const disabled = !this.resourceSelectorPropsForColumn(model, index).value;
      return {
        disabled,
        value: options.find(option => option.fact === column.fact),
        options
      };
    },
    pollSelectorPropsForColumn(model, index) {
      const column = model[index];
      const resources = this.polls;
      const selectedGuide = resources.find(guide => guide.guideId === column.guideId);
      if (!selectedGuide) {
        return {
          options: [],
          value: null,
          disabled: true
        };
      }
      const options = selectedGuide.polls;
      const value = options.find(question => question.id === column.pollId);
      return {
        disabled: false,
        options,
        value
      };
    },
    resourceSelectorPropsForColumn(model, index) {
      const column = model[index];
      const type = column.type === 'poll' ? 'guide' : column.type;
      const field = `${type}Id`;
      const options = this[`${column.type}s`];
      const placeholder = `Select ${startCase(column.type)}`;
      const value = options.find(option => option[field] === column[field]);
      return {
        placeholder,
        column,
        field,
        options,
        value
      };
    },
    onConfirm() {
      const formattedColumnsForTable = this.selectedColumns.reduce((acc, column) => {
        if (Object.values(column).every(val => val === undefined) || column.immutable) {
          return acc;
        }
        let label = column.name;
        let fact;
        if (isProductOption(column)) {
          const type = column.type === 'poll' ? 'guide' : column.type;
          const field = `${type}Id`;
          if (!column[field]) {
            return acc;
          }
          const resource = this[`${type}s`].find(resource => resource[field] === column[field]);
          if (column.fact) {
            fact = getFactOptionsForType(column.type).find(fact => fact.fact === column.fact);
            label = `${fact.label} for ${startCase(`${column.type} ${resource.name}`)}`;
          } else if (column.pollId) {
            const selectedPoll = resource.polls.find(question => question.id === column.pollId);
            column.poll = selectedPoll;
            label = `Poll Response for ${startCase(`${resource.name}`)} - ${selectedPoll.question}`;
          }
        }
        acc.push(_objectSpread2(_objectSpread2({}, column), {}, {
          prop: column.id,
          kind: column.kind,
          schema: column.schema,
          field: column.field,
          label,
          sortable: true
        }));
        return acc;
      }, []);
      this.$emit('confirm', formattedColumnsForTable);
    }
  }
};

export { script as default };
//# sourceMappingURL=pendo-resource-column-chooser.vue2.js.map
